import type React from 'react';
import { memo, useCallback, useMemo } from 'react';
import { cn } from '../../../../helpers/util';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { addNode, selectShowControlsInSingleRow } from '../../../../redux/slices/diagram';
import PlusIcon from '../../edge/icons/plus-icon.component';

interface AddEscalationFactorControlProps {
  id: string;
  controlRecordId?: number;
  index: number;
  disabled: boolean;
}

/**
 * A control component that renders a plus icon for adding an escalation factor.
 *
 * @component
 * @returns {React.JSX.Element} A rendered plus icon control
 *
 * @example
 * ```tsx
 * <div className="group">
 *   <Node {...props} />
 *   <AddEscalationFactorControl />
 * </div>
 * ```
 */
const AddEscalationFactorControl = ({
  id,
  controlRecordId,
  index = 0,
  disabled,
}: AddEscalationFactorControlProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const showControlsInSingleRow = useAppSelector(selectShowControlsInSingleRow);

  const isAboveEdge = useMemo(() => {
    const modY = showControlsInSingleRow ? 0 : index % 2;
    return modY !== 0;
  }, [index, showControlsInSingleRow]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) {
        return;
      }

      dispatch(
        addNode({
          type: 'escalation-factor-node',
          data: { id: crypto.randomUUID(), controlNodeId: id, controlRecordId: controlRecordId },
        })
      );
    },
    [disabled, dispatch, id, controlRecordId]
  );

  const className = cn(
    'nopan nodrag invisible absolute left-[calc(50%-10px)] z-10 cursor-pointer group-focus-within:visible group-hover:visible',
    { hidden: disabled },
    { '-bottom-4': isAboveEdge },
    { '-top-4': !isAboveEdge }
  );

  return (
    <div
      data-cy="add-escalation-factor-control"
      title="Add Escalation Factor"
      className={className}
      role="button"
      onClick={handleClick}
    >
      <PlusIcon className="fill-cline stroke-cline text-background" />
    </div>
  );
};

export default memo(AddEscalationFactorControl);
